import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookForm } from '@/components/books'
import { bookSelectOption } from '@/components/books/bookSelectOption'
import { BookThumb } from '@/components/insights'
import { Button, Select } from '@/components/ui'
import { useCreateBook, useUpdateBook } from '@/hooks/books'
import { useRefreshBoard, useRemoveEntry, useSetEntry } from '@/hooks/challenges'
import { useUpdateBookGenres } from '@/hooks/genres'
import { isInPeriod } from '@/lib/challenges'
import type { LibraryBook } from '@/lib/insights'
import { captureError } from '@/lib/monitoring'
import { todayISO } from '@/lib/utils'
import type { ChallengeCategory, ChallengeEntry, ClubChallenge, CreateBook } from '@/types'
import { formatDay } from './ChallengeMeta'

interface MyEntryPanelProps {
  challenge: ClubChallenge
  category: ChallengeCategory
  myEntry: ChallengeEntry | undefined
  myEntries: ChallengeEntry[]
  myBooks: LibraryBook[]
}

/** Pick a book for this category, mark it read, swap or remove it */
export function MyEntryPanel({
  challenge,
  category,
  myEntry,
  myEntries,
  myBooks,
}: MyEntryPanelProps) {
  const { t, i18n } = useTranslation()
  const setEntry = useSetEntry()
  const removeEntry = useRemoveEntry()
  const updateBook = useUpdateBook()
  const createBook = useCreateBook()
  const updateGenres = useUpdateBookGenres()
  const refreshBoard = useRefreshBoard()
  const [picking, setPicking] = useState(!myEntry)
  const [selected, setSelected] = useState<string | null>(null)
  const [addingBook, setAddingBook] = useState(false)
  const [error, setError] = useState(false)

  const myBook = myBooks.find((b) => b.id === myEntry?.book_id)
  const today = todayISO()
  const canFinishToday = isInPeriod(challenge, today)

  // A book can only count for one category: hide the ones used elsewhere
  const options = useMemo(() => {
    const usedElsewhere = new Set(
      myEntries.filter((e) => e.category_id !== category.id).map((e) => e.book_id)
    )
    return myBooks
      .filter((b) => !usedElsewhere.has(b.id))
      .sort((a, b) => a.title.localeCompare(b.title))
      .map((b) => bookSelectOption(b, { showStatus: true }))
  }, [myBooks, myEntries, category.id])

  const run = async (action: () => Promise<unknown>) => {
    setError(false)
    try {
      await action()
    } catch (e) {
      captureError(e as Error, { context: 'MyEntryPanel' })
      setError(true)
    }
  }

  const pick = (bookId: string) =>
    run(async () => {
      await setEntry.mutateAsync({ challengeId: challenge.id, categoryId: category.id, bookId })
      setPicking(false)
      setSelected(null)
    })

  const markReadToday = () =>
    run(async () => {
      if (!myBook) return
      await updateBook.mutateAsync({
        bookId: myBook.id,
        data: { status: 'read', end_date: today, start_date: myBook.start_date ?? today },
      })
      refreshBoard(challenge.id)
    })

  const handleNewBook = (data: CreateBook, genreIds: string[]) =>
    run(async () => {
      const book = await createBook.mutateAsync(data)
      if (genreIds.length) await updateGenres.mutateAsync({ bookId: book.id, genreIds })
      setAddingBook(false)
      await pick(book.id)
    })

  const statusText = () => {
    if (myEntry?.completed_on) {
      return `✅ ${t('challenges.entry.readOn', { date: formatDay(myEntry.completed_on, i18n.language) })}`
    }
    if (myBook?.status === 'read' && myBook.end_date) {
      return `⚠️ ${t('challenges.entry.readOutsidePeriod', { date: formatDay(myBook.end_date, i18n.language) })}`
    }
    if (myBook?.status === 'reading') return `📖 ${t('challenges.entry.reading')}`
    return `📚 ${t('challenges.entry.notRead')}`
  }

  if (addingBook) {
    return (
      <div className="space-y-3 rounded-lg border border-primary/30 p-4">
        <p className="text-sm font-medium text-foreground">{t('challenges.entry.newBook')}</p>
        <BookForm
          onSubmit={handleNewBook}
          onCancel={() => setAddingBook(false)}
          isLoading={createBook.isPending || setEntry.isPending}
          initialData={{ type: 'physical' }}
        />
      </div>
    )
  }

  return (
    <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
      <p className="text-sm font-semibold text-foreground">{t('challenges.entry.mine')}</p>

      {myEntry && !picking ? (
        <>
          <div className="flex items-center gap-3">
            <BookThumb src={myEntry.book_cover_url} title={myEntry.book_title} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{myEntry.book_title}</p>
              {myEntry.book_author && (
                <p className="truncate text-xs text-muted-foreground">{myEntry.book_author}</p>
              )}
              <p className="text-xs text-muted-foreground">{statusText()}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {myBook && !myEntry.completed_on && myBook.status !== 'read' && (
              <Button
                size="sm"
                variant="success"
                disabled={!canFinishToday || updateBook.isPending}
                title={canFinishToday ? undefined : t('challenges.entry.outsidePeriodHint')}
                onClick={markReadToday}
              >
                {t('challenges.entry.markReadToday')}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setPicking(true)}>
              {t('challenges.entry.change')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-error"
              disabled={removeEntry.isPending}
              onClick={() => run(() => removeEntry.mutateAsync(myEntry))}
            >
              {t('challenges.entry.remove')}
            </Button>
          </div>
          {!myBook && (
            <p className="text-xs text-muted-foreground">{t('challenges.entry.bookDeleted')}</p>
          )}
        </>
      ) : (
        <>
          <Select
            aria-label={t('challenges.entry.pickBook')}
            value={selected}
            onChange={setSelected}
            placeholder={
              options.length ? t('challenges.entry.pickBook') : t('challenges.entry.noBooks')
            }
            options={options}
            disabled={options.length === 0}
            searchable
            searchPlaceholder={t('search.searchByTitleAuthor')}
            emptyText={t('common.noResultsMessage')}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={!selected || setEntry.isPending}
              onClick={() => selected && pick(selected)}
            >
              {t('challenges.entry.pick')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAddingBook(true)}>
              + {t('challenges.entry.addNewBook')}
            </Button>
            {myEntry && (
              <Button size="sm" variant="ghost" onClick={() => setPicking(false)}>
                {t('common.cancel')}
              </Button>
            )}
          </div>
        </>
      )}

      {error && <p className="text-sm text-error">{t('errors.general')}</p>}
    </div>
  )
}
